﻿import { autorun } from "mobx";
import { Extent } from 'ol/extent';
import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import { Options as TileOptions } from 'ol/layer/BaseTile';
import TileLayer from "ol/layer/Tile";
import TileSource from "ol/source/Tile";
import TileWMS from "ol/source/TileWMS";
import WMTS, { optionsFromCapabilities } from 'ol/source/WMTS';
import { ExternalLayerTypes } from "../../components/externalLayers/LayerTypes";
import { ChartStates } from '../../models/ChartStates';
import { MobxReactionDisposer } from "../../models/MobxReactionDisposer";

interface Options extends Omit<TileOptions<TileSource>, 'extent'> {
    extent: Extent,
    source?: TileWMS
}

class ExternalLayer extends TileLayer<TileSource> {

    protected mobxDisposer = new MobxReactionDisposer();

    constructor(
        private states: ChartStates,
        options: Options) {
        super(options);

        this.mobxDisposer.add([
            autorun(() => {
                if (!states.external)
                    return;

                this.setVisible(states.external.selectedLayerId !== -1);
            }),

            autorun(async () => {
                this.setSource(await this.sourceLayer() as any);
            })
        ])        
    }

    public dispose() {
        this.mobxDisposer.clear();
        super.dispose();
    }

    private sourceLayer = async () => {
        const chartLayer = this.states.external?.layers?.find(l => l.id === this.states.external?.selectedLayerId)
        if (!chartLayer) {
            return undefined;
        }

        //if none is selected
        if (chartLayer.id === -1)
            return undefined;

        if (!chartLayer.url || chartLayer.url === "") {
            console.error("url is undefined");
            return undefined;
        }

        const layers = typeof chartLayer.layers === 'string' ? [chartLayer.layers] : chartLayer.layers;

        if (layers.length === 0) {
            console.error("chartLayer layers is empty");
            return undefined;
        }

        if (!chartLayer.capabilitiesUrl || chartLayer.capabilitiesUrl === "") {
            console.error("capabilitiesUrl is undefined");
            return undefined;
        }

        //TODO
        switch (chartLayer.type) {
            case ExternalLayerTypes.WMS:
                return new TileWMS({
                    url: chartLayer.url,
                    params: { 'LAYERS': layers.join(","), 'TILED': true },
                    transition: 0
                });
            case ExternalLayerTypes.WMTS: {
                
                try {
                    const parser = new WMTSCapabilities();
                    const response = await fetch(chartLayer.capabilitiesUrl);

                    const readCapabilities = parser.read(await response.text());

                    let matrixSet = undefined;
                    if (chartLayer.matrixSet || chartLayer.matrixSet !== "")
                        matrixSet = chartLayer.matrixSet;                   

                    const options = optionsFromCapabilities(readCapabilities, {
                        layer: layers[0],
                        matrixSet: matrixSet
                    });

                    if (!options) {
                        console.error("optionsFromCapabilities is null");
                        return undefined;
                    }

                    return new WMTS(options as any);
                } 
                catch (e) {
                    console.error("Capabilities could not be read", e);
                    return undefined;
                }
                
            }
            default:
                console.error("ExternalLayer: unknown layer type");
        }

    }
}

export default ExternalLayer;